/**
 * Pay Data Transform Service
 * Transforms raw API pay data for UI consumption
 */

/**
 * Extract provider pay data by doctor abbreviation
 * @param {Object} payData - Raw pay data from API
 * @param {string} abbr - Doctor abbreviation
 * @returns {Object} Provider pay data for the specific doctor
 */
export const extractProviderPayByDoctor = (payData, abbr) => {
  if (!payData || !payData.groupByDoctorByPractice || !abbr) {
    return {};
  }
  
  const normalizedAbbr = abbr.toUpperCase();
  const providerPay = payData.groupByDoctorByPractice.find(
    iRow => iRow.ABBR?.toUpperCase() === normalizedAbbr
  );
  
  return providerPay || {};
};

/**
 * Sort financial notes by practice and date
 * @param {Array} financialNoteList - Array of financial notes
 * @returns {Array} Sorted financial notes
 */
export const sortFinancialNotes = (financialNoteList) => {
  if (!Array.isArray(financialNoteList)) {
    return [];
  }
  
  return financialNoteList.sort((noteA, noteB) => {
    if (noteA.practice === noteB.practice) {
      return noteA.date - noteB.date;
    }
    return noteA.practice.localeCompare(noteB.practice);
  });
};

/**
 * Flatten calendar list from earnings array
 * @param {Array} earningsArray - Array of practice earnings
 * @returns {Array} Flattened calendar list
 */
export const flattenCalendarList = (earningsArray) => {
  if (!Array.isArray(earningsArray)) {
    return [];
  }
  
  return earningsArray
    .map(iPractice => iPractice.calendarList || [])
    .flat();
};

/**
 * Get contract type from provider pay data
 * @param {Object} providerPayByDoctor - Provider pay data
 * @returns {string|null} Contract type name
 */
export const getContractType = (providerPayByDoctor) => {
  return providerPayByDoctor?.contractType || null;
};

/**
 * Get pay billable percentage from provider pay data
 * @param {Object} providerPayByDoctor - Provider pay data
 * @returns {number} Pay billable percentage (0-100)
 */
export const getPayBillablePercentage = (providerPayByDoctor) => {
  return providerPayByDoctor?.payOverBase || 0;
};

/**
 * Process complete provider pay data
 * @param {Object} payData - Raw pay data from API
 * @param {string} abbr - Doctor abbreviation
 * @returns {Object} Processed provider pay data with sorted notes
 */
export const processProviderPayData = (payData, abbr) => {
  const providerPay = extractProviderPayByDoctor(payData, abbr);

  // Sort financial notes if they exist
  if (providerPay.financialNoteList) {
    providerPay.financialNoteList = sortFinancialNotes(providerPay.financialNoteList);
  }

  // Defensive frontend purge: remove any nested entries that reference excluded procedure codes
  try {
    const normalize = s => (s || '').toString().trim().toUpperCase();

    // Priority: payload excluded codes -> cached window value -> async fetch to populate cache
    const payloadExcluded = (payData && Array.isArray(payData.excludedCodes)) ? payData.excludedCodes.map(c => normalize(c)) : null;
    let excludedCodes = Array.isArray(payloadExcluded) && payloadExcluded.length ? payloadExcluded : (window.__EXCLUDED_PROC_CODES__ || []);

    // If not provided in payload, expect top-level component to have populated
    // `window.__EXCLUDED_PROC_CODES__` via a Redux call on mount.

    if (excludedCodes && excludedCodes.length) {
      // Purge provider data per practice
      if (providerPay.data && Object.keys(providerPay.data).length) {
        Object.keys(providerPay.data).forEach(prac => {
          const arr = providerPay.data[prac];
          if (Array.isArray(arr) && arr.length) {
            providerPay.data[prac] = arr.filter(item => {
              const code = normalize(item.ProcCode || item.procCode || item.procedureCode || item.proccode || item.Proccode);
              return !code || !excludedCodes.includes(code);
            });
          }
        });
      }

      // Purge dataCollection
      if (providerPay.dataCollection && Object.keys(providerPay.dataCollection).length) {
        Object.keys(providerPay.dataCollection).forEach(prac => {
          const arr = providerPay.dataCollection[prac];
          if (Array.isArray(arr) && arr.length) {
            providerPay.dataCollection[prac] = arr.filter(item => {
              const code = normalize(item.ProcCode || item.procCode || item.procedureCode || item.proccode || item.Proccode);
              return !code || !excludedCodes.includes(code);
            });
          }
        });
      }

      // Purge dataBCare
      if (Array.isArray(providerPay.dataBCare) && providerPay.dataBCare.length) {
        providerPay.dataBCare = providerPay.dataBCare.filter(item => {
          const code = normalize(item.ProcCode || item.procCode || item.procedureCode || item.proccode || item.Proccode);
          return !code || !excludedCodes.includes(code);
        });
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[PROC-EXCLUDE][frontend] error purging providerPay payload', e);
  }

  return providerPay;
};

/**
 * Check if provider has worked in two practices on the same day
 * @param {Object} item - Calendar item
 * @returns {boolean} True if provider worked in two practices
 */
export const hasWorkedInTwoPractices = (item) => {
  return item?.hasProviderWorkedInTwoPracticesToday || false;
};
